import moneyHelper from '@/helpers/moneyHelper';
import PaymentTypes from '@/models/PaymentTypes';

export default {
    namespaced: true,

    state: {
        PaymentType: null,
        PaymentMethodId: null,
        NumberOfInstallments: null,
        TotalPrice: null,
        Discount: null,
        ShippingPrice: null,
        PaymentMethods: null,
        MinimumInstallmentAmount: null,
        FixedInstallments: null,
        SelectedPaymentMethodInstallmentIsAbleToBeFixed: false
    },

    getters: {
        /**
         * Retorna o número de parcelas.
         */
        getInstallmentNumberOfPayments: state => state.NumberOfInstallments || 0,

        /**
         * Retorna o preço total já removendo o desconto
         */
        getTotalPrice: (state, getters, rootstate, rootGetters) => {
            const shippingPrice = state.ShippingPrice || 0;
            const subtotal = (state.TotalPrice || 0) + shippingPrice;
            let discountAmount =  rootGetters['discount/getDiscountAmount'] || 0;
            const total = subtotal - discountAmount;

            return total;
        },

        /**
         * Retorna o preço total sem o frete já removendo o desconto
         */
        getTotalPriceWithoutShipping: (state, getters, rootstate, rootGetters) => {
            const subtotal = (state.TotalPrice || 0);
            let discountAmount =  rootGetters['discount/getDiscountAmount'] || 0;
            const total = subtotal - discountAmount;

            return total;
        },

        /**
         * Retorna a lista de installments apple pay com base no allowedinstallments
         */
        getApplePayInstallmentList(state, getters, rootState, rootGetters) {
            let maxNumberOfPayments = rootGetters['applePayPayment/getAllowedInstallments'];
            let calcMaxInstallments = Math.max(1, Math.floor(getters.getTotalPriceWithoutShipping / (state.MinimumInstallmentAmount / 100)));
            maxNumberOfPayments= Math.min(maxNumberOfPayments, calcMaxInstallments);

            let accumulator = [];
            let fixedInstallments = state.FixedInstallments;

            // Calcula o preço das parcelas
            for (let quantity = 1; quantity <= maxNumberOfPayments; quantity++) {
                let installmentPrice = moneyHelper.formatToPrice(getters.getTotalPrice / quantity);
                accumulator.push({
                    Value: quantity,
                    installmentPrice: installmentPrice
                });
            }

            if(accumulator.length === 1) state.NumberOfInstallments = 1;

            if(fixedInstallments) {
                if(accumulator.length >= fixedInstallments) {
                    state.SelectedPaymentMethodInstallmentIsAbleToBeFixed = true;
                    state.NumberOfInstallments = fixedInstallments;
                    accumulator = accumulator.filter(item => item.Value === fixedInstallments);
                } else {
                    state.SelectedPaymentMethodInstallmentIsAbleToBeFixed = false;
                }
            }

            return accumulator;
        },

        /**
         * Retorna a lista de installments para o meio de pagamento selecionado
         */
        getInstallmentListForPaymentMethodSelected(state, getters) {
            let selectedPaymentMethod = state.PaymentMethodId || 0;
            return getters.getInstallmentListByPaymentMethodId(selectedPaymentMethod);
        },

        /**
         * Retorna a lista de installments para um determinado meio de pagamento
         */
        getInstallmentListByPaymentMethodId: (state, getters) => (paymentMethodId) => {
            if (!state.PaymentMethods) return;
            if (!paymentMethodId) return;

            let paymentMethod = state.PaymentMethods.filter(item => item.Id == paymentMethodId)[0];
            let maxNumberOfPayments = paymentMethod.MaxNumberOfPayments;
            let calcMaxInstallments = Math.max(1, Math.floor(getters.getTotalPriceWithoutShipping / (state.MinimumInstallmentAmount / 100)));
            maxNumberOfPayments= Math.min(maxNumberOfPayments, calcMaxInstallments);

            let accumulator = [];
            let fixedInstallments = state.FixedInstallments;

            // Calcula o preço das parcelas
            for (let quantity = 1; quantity <= maxNumberOfPayments; quantity++) {
                let installmentPrice = moneyHelper.formatToPrice(getters.getTotalPrice / quantity);
                accumulator.push({
                    Value: quantity,
                    installmentPrice: installmentPrice
                });
            }

            if(accumulator.length === 1) state.NumberOfInstallments = 1;

            if(fixedInstallments) {
                const allowedPaymentTypes = [
                    PaymentTypes.CREDIT_CARD,
                    PaymentTypes.QRCODE,
                    PaymentTypes.CLICK_TO_PAY,
                    PaymentTypes.GOOGLE_PAY,
                    PaymentTypes.APPLE_PAY
                ];

                let isAbleToBeFixed = allowedPaymentTypes.includes(paymentMethod.PaymentType);

                if(isAbleToBeFixed && accumulator.length >= fixedInstallments) {
                    state.SelectedPaymentMethodInstallmentIsAbleToBeFixed = true;
                    state.NumberOfInstallments = fixedInstallments;
                    accumulator = accumulator.filter(item => item.Value === fixedInstallments);
                } else {
                    state.SelectedPaymentMethodInstallmentIsAbleToBeFixed = false;
                }
            }

            return accumulator;
        },

        /**
         * Retorna o valor das parcelas.
         */
        getFormattedInstallmentPrice(state, getters) {
            let numberOfPayments = state.NumberOfInstallments || 1;
            let totalPrice = getters.getTotalPrice;

            if (totalPrice == 0)
                return moneyHelper.formatToPrice(0);

            return moneyHelper.formatToPrice(totalPrice / numberOfPayments);
        },

        /**
         * Retorna o preço total formatando as casas decimais.
         */
        getFormattedPrice: (state, getters) => moneyHelper.formatToPrice(getters.getTotalPrice),

        /**
         * Retorna o subtotal formatando as casas decimais.
         */
        getFormattedSubtotalPrice: (state) => moneyHelper.formatToPrice(state.TotalPrice),

        /**
         * Retorna um meio de pagamento pelo ID
         */
        getPaymentMethodById: (state) => (paymentMethodId) => {
            if (!state.PaymentMethods)
                return null;

            return state.PaymentMethods.filter(item => item.Id == paymentMethodId)[0];
        },

        /**
         * Retorna o nome de um tipo de pagamento pelo id
         */
        getPaymentTypeNameById: (state) => (paymentMethodId) => {
            switch (paymentMethodId) {
            case PaymentTypes.CREDIT_CARD:
                return 'Cartão de crédito';
            case PaymentTypes.BOLETO:
                return 'Boleto';
            case PaymentTypes.ONLINE_DEBIT:
                return 'Débito Online';
            case PaymentTypes.DEBIT_CARD:
                return 'Cartão de débito';
            case PaymentTypes.QRCODE:
                return 'QR Code™';
            case PaymentTypes.CLICK_TO_PAY:
                return 'Click To Pay™';
            case PaymentTypes.MULTIBENEFITS:
                return 'Multibenefícios';
            case PaymentTypes.GOOGLE_PAY:
                return 'Google Pay™';
            case PaymentTypes.APPLE_PAY:
                return 'Apple Pay™';
            default:
                return 'Não identificado';
            }
        },

        /**
         * Retorna o meio de pagamento selecionado
         */
        getSelectedPaymentMethod(state, getters) {
            const selectedPaymentMethod = state.PaymentMethodId || 0;
            const paymentMethod = getters.getPaymentMethodById(selectedPaymentMethod);

            if (paymentMethod) {
                return {
                    ...paymentMethod,
                    Discount: state.Discount,
                    TypeName: getters.getPaymentTypeNameById(paymentMethod.PaymentType),
                    PaymentMethod: paymentMethod
                };
            }

            return null;
        },

        /**
         * Retorna a bandeira do meio de pagamento selecionado, ou nulo caso não tenha
         */
        getSelectedCardBrand(state, getters) {
            return getters['getSelectedPaymentMethod'].Image || null;
        },

        /**
         * Retorna o id do meio de pagamento selecionado, ou nulo caso não tenha
         */
        getSelectedPaymentTypeId(state, getters) {
            return getters['getSelectedPaymentMethod'].PaymentMethod.PaymentType || null;
        },

        /**
         * Monta o contrato de autorização
         */
        getSubmitModel(state, getters, rootState, rootGetters) {
            const isRecurringPayment = rootGetters['recurrentPayment/isRecurringPayment'];
            const installments = getters['getInstallmentNumberOfPayments'];

            const paymentType = Number(state.PaymentType);

            return {
                Installments: isRecurringPayment ||
				paymentType === 6 ||
				paymentType === 2 ||
				paymentType === 7 ||
				paymentType === 4 ||
				paymentType === 8 ||
				paymentType === 9 ? 1 : installments,
                PaymentType: state.PaymentType,
                PaymentMethodId: state.PaymentMethodId
            };
        },

        getDiscount(state) {
            return state.Discount || 0;
        },

        /**
         * Retorna se o método de pagamento selecionado tem parcela fixa
         */
        getSelectedPaymentMethodInstallmentIsAbleToBeFixed(state) {
            return state.SelectedPaymentMethodInstallmentIsAbleToBeFixed;
        }
    },

    actions: {
        updateTotalPrice({ commit, rootGetters }) {
            let cartTotalPrice = rootGetters['shoppingCart/getCardItemsTotalPrice'];
            let formattedPrice = cartTotalPrice / 100; // Divide por 100 para adicionar os centavos.

            commit('setTotalPrice', formattedPrice);
        },

        setPaymentMethod({ state, getters }, paymentMethodId) {
            let paymentMethod = getters.getPaymentMethodById(paymentMethodId);
            if (!paymentMethod)
                throw new Error('Meio de pagamento não suportado');

            state.PaymentType = paymentMethod.PaymentType;
            state.PaymentMethodId = paymentMethod.Id;
        },

        setShippingPrice({ commit }, shippingPrice) {
            commit('setShippingPrice', shippingPrice);
        },
    },

    mutations: {
        /**
         * Mapeia o response da api para o módulo
         */
        setFromRequest(state, data) {
            state.PaymentMethods = data.PaymentMethods;
            state.MinimumInstallmentAmount = data.MinimumInstallmentAmount;
            state.FixedInstallments = data.FixedInstallments;
        },

        setTotalPrice: (state, totalPrice) => state.TotalPrice = totalPrice,
        setNumberOfInstallments: (state, numberOfInstallments) => state.NumberOfInstallments = numberOfInstallments,

        setPaymentMethodNumberOfInstallments: (state, payload) => {
            state.PaymentMethods.filter(item => item.Id == payload.paymentMethodId)[0].MaxNumberOfPayments = payload.maxNumberOfPayments || 1;
        },

        setShippingPrice(state, shippingPrice) {
            state.ShippingPrice = 0;
            state.ShippingPrice = shippingPrice / 100;
        },

        setPaymentType(state, paymentType) {
            state.PaymentType = paymentType;
        },

        setDiscount(state, discount) {
            state.Discount = discount || 0;
        },

        resetPaymentMethod(state) {
            state.PaymentType = null;
            state.PaymentMethodId = null;
            state.Discount = null;
        },
        resetMethod(state) {
            state.PaymentMethodId = null;
        }
    }
};
